import { createRootRouteWithContext, Outlet, useRouterState } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { BootScreen } from "@/components/layout/BootScreen";
import { useInstanceStore } from "@/stores/instance";
import { useAuthStore } from "@/stores/auth";
import { startRealtime, stopRealtime, resumeRealtime } from "@/lib/realtime";
import { useBrand, useCustomStyle, useCustomScript } from "@/stores/instance";
import { ErrorBoundary, ChunkErrorBoundary } from "@/components/layout/ErrorBoundary";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: () => null, // handled by the 404 route
});

function RootComponent() {
  const instanceStatus = useInstanceStore((s) => s.status);
  const authStatus = useAuthStore((s) => s.status);
  const brand = useBrand();
  const customStyle = useCustomStyle();
  const customScript = useCustomScript();
  const location = useRouterState({ select: (s) => s.location.pathname });

  // Brand the document.
  useEffect(() => {
    document.title = brand.title;
  }, [brand.title]);

  // Start/stop realtime with auth state. The ref prevents double-subscription
  // when React StrictMode remounts the component in development.
  const realtimeStarted = useRef(false);
  useEffect(() => {
    if (authStatus === "authenticated" && !realtimeStarted.current) {
      realtimeStarted.current = true;
      startRealtime();
      const onVisible = () => {
        if (document.visibilityState === "visible") resumeRealtime();
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        document.removeEventListener("visibilitychange", onVisible);
        stopRealtime();
        realtimeStarted.current = false;
      };
    }
    if (authStatus !== "authenticated") {
      stopRealtime();
      realtimeStarted.current = false;
    }
  }, [authStatus]);

  // Wait for instance profile + session restore before rendering the shell so
  // protected pages never flash sensitive content (PRD 2.2).
  const booting = instanceStatus === "idle" || instanceStatus === "loading" || authStatus === "unknown" || authStatus === "restoring";

  return (
    <ChunkErrorBoundary key={location}>
      <ErrorBoundary>
        {/* Administrator-configured custom CSS (trusted instance-level code). */}
        {customStyle ? <style data-custom-style>{customStyle}</style> : null}
        {booting ? (
          <BootScreen />
        ) : (
          <AppLayout>
            <Outlet />
          </AppLayout>
        )}
        {/* Custom script is an explicit, admin-granted instance-level capability. */}
        {customScript ? <CustomScript script={customScript} /> : null}
      </ErrorBoundary>
    </ChunkErrorBoundary>
  );
}

function CustomScript({ script }: { script: string }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.dataset.customScript = "true";
    el.text = script;
    document.body.appendChild(el);
    return () => {
      el.remove();
    };
  }, [script]);
  return null;
}
